import Image from "next/image";
import { useTrans } from "@/utils/useTrans";
import { DEVICE_CARDS } from "./sw-config";

/**
 * Software 히어로 — 제목 + 기기 선택 카드 3개 (시안 `29889:102289` · `30035:23199`).
 *
 * ## 두 폭의 값이 다르다
 *
 * ```
 *              데스크탑 29889:102289          모바일 30035:23199
 * 컨테이너      pt-96 · gap-32                 px-16 · gap-20
 * 제목          45/52 cg-100 (한 줄)           24/32 cg-100 (두 줄)
 * 설명          14/18 cg-60                    12/16 cg-70
 * 카드          464×372 · 가로 3 · 간격 24      전체폭 h170 · 세로 3 · 간격 16
 * 카드 제목      36/44 white                    22/28 white
 * 카드 부제      14/18 SemiBold white/86        12/16 cg-20
 * CTA           blur5 · white/12 · 보더 white/26 · r999 · py-12 px-25
 *                                              bg #3C444B · h34 · r44 · px-16
 * ```
 *
 * 카드는 **같은 페이지 앵커**로 내려가는 링크다(`#sw-pc-player` 등).
 */
export default function SwHeadBand() {
  const { t } = useTrans(["software"]);

  return (
    <section className="flex w-full justify-center">
      <div className="mx-4 flex w-full max-w-screen-max flex-col items-center gap-5 laptop:gap-8">
        {/* ★시안 실측: **두 폭 모두 가운데 정렬**이다(초판은 모바일을 좌측으로 뒀다). */}
        {/* 최대 너비를 두지 않는다(작성자 결정) — 시안은 `shell sw-head` 가 `px-[478px]` 로
            폭을 좁히지만, 로케일에 따라 제목이 길어져 줄바꿈이 갈리는 것을 피한다. */}
        <div className="flex w-full flex-col gap-2 text-center laptop:gap-4">
          <h1 className="text-3xl text-coolgray-100 laptop:text-7xl">
            {t("Choose the Right Player for Your VR Device")}
          </h1>
          {/**
           * ★**설명은 한 문장이다** — 종전 주석은 *"시안도 `<p>` 두 개다"* 라며 두 번째
           * 줄(`All three players are free.`)을 뒀는데, 시안을 다시 떠 보니
           * `29889:102293`(데스크탑)·`30035:23202`(모바일) 둘 다 **텍스트 노드가 하나**다.
           * 그 문장은 **시안에 대응물이 없었다** — 작성자 결정으로 걷었다(2026-09-03).
           *
           * 최대 너비를 두지 않는 것은 그대로다(로케일에 따라 제목 줄바꿈이 갈리는 것을 피한다).
           */}
          <p className="text-sm tracking-[0.4px] text-coolgray-70 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-60">
            {t(
              "Connect TactSuit and bHaptics devices with the right Player for PC VR, standalone VR, and PS VR2.",
            )}
          </p>
        </div>

        {/**
         * ★**카드 레일은 껍데기를 채운다** — 종전 `laptop:w-auto` + 카드 고정폭이라
         * 1024~1471 에서 **화면 밖으로 나갔다**(작성자 제보 2026-09-04).
         *
         * 시안은 1440 껍데기에 464×3 + 갭 24×2 = **정확히 1440** 이다. 그 값을
         * 고정폭으로 옮기면 껍데기가 그보다 좁아져도 1440 을 그대로 버틴다:
         *
         * ```
         *   실측 1100:  레일 w=1440 · x=-170  ← 첫 카드 왼쪽이 잘려 안 보임
         *              문서 가로 스크롤 발생 (1270 > 1100)
         * ```
         *
         * `w-full` + 카드 `flex-1` 로 바꿔 셋이 함께 줄어들게 한다. 비율은 아래
         * 카드의 `aspect-[464/372]` 가 지킨다.
         */}
        <div className="flex w-full flex-col gap-4 laptop:flex-row laptop:gap-6">
          {DEVICE_CARDS.map((card) => (
            <a
              key={card.anchor}
              href={`#${card.anchor}`}
              /**
               * 부드럽게 이동한다(작성자 요청). `scroll-behavior: smooth` 는 **스크롤
               * 컨테이너**(html/body)에 걸어야 먹으므로 Tailwind 클래스를 이 링크에
               * 붙여선 안 된다 — 전역 CSS 를 건드리는 대신 이 자리에서만 처리한다.
               * 섹션의 `scroll-mt-28` 이 sticky 헤더 가림을 함께 막는다.
               */
              onClick={(e) => {
                const target = document.getElementById(card.anchor);
                if (!target) return;
                e.preventDefault();
                /**
                 * ★sticky 헤더 높이를 **클릭 시점에 잰다.** 고정 `scroll-mt` 로 두면
                 * 어긋난다 — 헤더는 로케일 제안 배너·레퍼럴 배너·프로모 배너 유무로
                 * 높이가 변한다(실측: 배너 없을 때 186, `scroll-mt-28`=112 라 74 가림).
                 */
                const sticky = document.querySelector<HTMLElement>(
                  "[data-sw-sticky-header]",
                );
                const offset =
                  (sticky?.getBoundingClientRect().height ?? 0) + 24;
                window.scrollTo({
                  top:
                    target.getBoundingClientRect().top +
                    window.scrollY -
                    offset,
                  behavior: "smooth",
                });
              }}
              /**
               * ★**데스크탑은 고정 크기가 아니라 비율이다**(작성자 제안 2026-09-04).
               *
               * 시안 464×372 = 비율 `464/372`. 종전엔 `laptop:h-[372px]
               * laptop:w-[464px]` 로 둘 다 고정이라 껍데기가 좁아져도 안 줄었다.
               * `flex-1` + `aspect` 로 바꾸면 셋이 같이 줄고 비율은 유지된다:
               *
               * ```
               *   뷰포트   껍데기   카드폭   카드높이
               *   1024     992      315      252
               *   1280    1248      400      321
               *   1472+   1440      464      372   ← 시안 값에 정확히 도달
               * ```
               *
               * (껍데기가 `mx-4 max-w-screen-max` 라 1472 부터 1440 으로 고정된다.)
               */
              className="group relative flex h-[170px] w-full flex-col items-start justify-end gap-2.5 overflow-hidden rounded-2xl p-5 laptop:aspect-[464/372] laptop:h-auto laptop:w-auto laptop:flex-1 laptop:justify-start laptop:gap-0 laptop:p-0"
            >
              {/* 카드 이미지도 폭별 자산이 따로 있다(시안). */}
              <Image
                src={card.image.mobile}
                alt=""
                fill
                sizes="100vw"
                className="hover-scale object-cover laptop:hidden"
              />
              {/* 카드가 비율로 줄어드므로 `sizes` 도 고정 464 가 아니라 3열 기준이다. */}
              <Image
                src={card.image.desktop}
                alt=""
                fill
                sizes="(min-width: 1472px) 464px, 33vw"
                className="hover-scale hidden object-cover laptop:block"
              />
              {/**
               * 시안 모바일은 카드 하단 정렬, 데스크탑은 좌측 28.
               *
               * ★**세로는 `top-[215px]` 이 아니라 바닥 기준이다.** 시안(464×372)의
               * 내용블록은 y=215 h=130 이라 **바닥 여백이 27**(≈ 좌우 28 과 같다).
               * 카드가 비율로 줄어드는데 `top` 을 절대값으로 두면 내용이 바닥을 넘는다:
               *
               * ```
               *   뷰포트   카드높이   top215+내용130   남는 바닥
               *   1024     252        345            -93   ← 넘침
               *   1280     321        345            -24   ← 넘침
               *   1472     372        345             27   ← 시안
               * ```
               *
               * `bottom-7`(28)로 잡으면 1472 에서 top 이 372−28−130 = **214** 로
               * 시안 215 와 맞고, 좁은 폭에서도 안 넘친다. 로케일에 따라 내용 높이가
               * 달라져도 바닥 기준이라 안전하다.
               */}
              <div className="relative flex w-full flex-col gap-2.5 laptop:absolute laptop:bottom-7 laptop:left-7 laptop:right-7 laptop:w-auto laptop:gap-5">
                <div className="flex flex-col gap-2.5 laptop:gap-[3px]">
                  <p className="text-2xl text-white laptop:text-6xl">
                    {t(card.title)}
                  </p>
                  {/**
                   * 부제 — ★**문장은 두 폭이 같고 타이포만 갈린다**(2026-09-04).
                   *
                   * 종전엔 `captionMobile` 을 따로 받았는데 개정 시안에서 **3/3 이
                   * 글자 단위로 같아** 판별력이 0 이었다. 그 필드를 걷고 `caption`
                   * 하나를 두 폭이 읽는다(`descriptionMobile` 과 같은 처리).
                   *
                   * 모바일 12/16 tr.4 cg-20 · 데스크탑 14/18 SemiBold white/86.
                   */}
                  <p className="text-sm tracking-[0.4px] text-coolgray-20 laptop:hidden">
                    {t(card.caption)}
                  </p>
                  {/* ★한 줄로 — 시안 박스가 180 이라 부제가 감겼다(부제 실폭 204).
                      너비 제약을 텍스트에서 떼고 **CTA 에만** 180 을 남긴다.
                      `nowrap` 은 쓰지 않는다 — 로케일이 길면 감겨야 한다. */}
                  <p className="hidden text-base font-600 tracking-[0.25px] text-white/[86%] laptop:block">
                    {t(card.caption)}
                  </p>
                </div>
                <span className="flex h-[34px] items-center justify-center self-start rounded-[44px] bg-[#3C444B] px-4 text-sm font-600 tracking-[0.4px] text-white laptop:h-auto laptop:w-[180px] laptop:self-start laptop:rounded-full laptop:border laptop:border-white/[26%] laptop:bg-white/[12%] laptop:px-[25px] laptop:py-3 laptop:tracking-[0.25px] laptop:backdrop-blur-[5px]">
                  {t(card.cta)}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
